import { createFileRoute } from '@tanstack/react-router'
import { useWindowVirtualizer } from '@tanstack/react-virtual'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Amount, Box, Copy, cx, Empty, Hash, Rows, Tag } from 'regen-ui'
import { maxUint256 } from 'viem'
import { useConnection } from 'wagmi'
import ArrowDownLeft from '~icons/lucide/arrow-down-left'
import ArrowDownToLine from '~icons/lucide/arrow-down-to-line'
import ArrowLeftRight from '~icons/lucide/arrow-left-right'
import ArrowUpRight from '~icons/lucide/arrow-up-right'
import Flame from '~icons/lucide/flame'
import Key from '~icons/lucide/key-round'
import ReceiptText from '~icons/lucide/receipt-text'
import ShieldCheck from '~icons/lucide/shield-check'
import Sparkles from '~icons/lucide/sparkles'
import Timer from '~icons/lucide/timer'
import X from '~icons/lucide/x'

import { useActivity } from '#/lib/activity.js'
import { checksumAddressForCopy } from '#/lib/address.js'
import * as Zone from '#/lib/zone.js'
import { ScreenLayout } from '#/routes/_app/-components/ScreenLayout.js'
import type { ActivityEntry, ActivityItem } from '../../../worker/lib/activity.js'

export const Route = createFileRoute('/_app/activity')({
  component: ActivityPage,
})

const displayNames: Record<string, string> = {
  send: 'Sent',
  received: 'Received',
  mint: 'Deposited',
  burn: 'Withdrew',
  deposit: 'Deposited',
  withdrawal: 'Withdrew',
  swap: 'Swapped',
  approval: 'Authorized',
  'session-closed': 'Session closed',
  'access-key-created': 'Access key created',
  'access-key-revoked': 'Access key revoked',
  'token-created': 'Token created',
}

const relative = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })

const rowHeight = 64

/** A flat row for virtualization — either an item, a group header, or a group child. */
type FlatRow =
  | { kind: 'item'; item: ActivityItem }
  | {
      kind: 'group-header'
      group: Extract<ActivityEntry, { type: 'group' }>
      expanded: boolean
      entryIndex: number
    }
  | { kind: 'group-child'; item: ActivityItem }

function ActivityPage() {
  const { address, chainId } = useConnection()
  const activity = useActivity({
    account: address ?? null,
    chainId: chainId!,
    enabled: typeof window !== 'undefined' && !!address,
  })

  const entries = useMemo(() => activity.data?.pages.flatMap((p) => p.items) ?? [], [activity.data])

  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set())
  const toggleGroup = useCallback((index: number) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }, [])

  const flatRows = useMemo(() => {
    const rows: FlatRow[] = []
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i]
      if (entry.type === 'group') {
        const expanded = expandedGroups.has(i)
        rows.push({ kind: 'group-header', group: entry, expanded, entryIndex: i })
        if (expanded) for (const item of entry.items) rows.push({ kind: 'group-child', item })
      } else {
        rows.push({ kind: 'item', item: entry })
      }
    }
    return rows
  }, [entries, expandedGroups])

  const count = flatRows.length
  const listRef = useRef<HTMLDivElement>(null)
  const listOffsetRef = useRef(0)

  useLayoutEffect(() => {
    listOffsetRef.current = listRef.current?.offsetTop ?? 0
  }, [])

  const virtualizer = useWindowVirtualizer({
    count,
    estimateSize: () => rowHeight,
    overscan: 5,
    scrollMargin: listOffsetRef.current,
  })

  const fetchNextPage = activity.fetchNextPage
  const hasNextPage = activity.hasNextPage
  const isFetchingNextPage = activity.isFetchingNextPage
  const isLoading = activity.isLoading

  useEffect(() => {
    const items = virtualizer.getVirtualItems()
    const lastItem = items[items.length - 1]
    if (!lastItem) return
    if (lastItem.index >= flatRows.length - 5 && hasNextPage && !isFetchingNextPage) fetchNextPage()
  }, [
    hasNextPage,
    fetchNextPage,
    flatRows.length,
    isFetchingNextPage,
    virtualizer.getVirtualItems(),
  ])

  return (
    <ScreenLayout heading={<h1 className="text-heading-24">Activity</h1>} walletActions={false}>
      <Box
        className="flex flex-col max-sm:-mx-4 sm:-mx-8 md:mx-0 max-md:rounded-none max-md:border-x-0"
        padding={0}
      >
        {isLoading ? (
          <Rows className="[&_[data-rows-divider]]:hidden" variant="surface">
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </Rows>
        ) : entries.length === 0 ? (
          <Empty icon={<ReceiptText />} title="No activity yet">
            Transactions, deposits, swaps, and app authorizations will appear here.
          </Empty>
        ) : (
          <Rows className="[&_[data-rows-divider]]:hidden" variant="surface">
            <div
              ref={listRef}
              className="relative w-full"
              style={{ height: virtualizer.getTotalSize() }}
            >
              {virtualizer.getVirtualItems().map((virtualRow) => (
                <div
                  className="absolute left-0 top-0 w-full"
                  key={virtualRow.index}
                  style={{
                    height: virtualRow.size,
                    transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
                  }}
                >
                  <FlatRowRenderer row={flatRows[virtualRow.index]} onToggle={toggleGroup} />
                </div>
              ))}
            </div>
            {isFetchingNextPage && (
              <>
                <SkeletonRow />
                <SkeletonRow />
              </>
            )}
          </Rows>
        )}
      </Box>
    </ScreenLayout>
  )
}

function FlatRowRenderer(props: { row: FlatRow; onToggle: (index: number) => void }) {
  const { row } = props
  switch (row.kind) {
    case 'item':
      return <ItemRow item={row.item} />
    case 'group-header':
      return (
        <GroupHeaderRow
          entry={row.group}
          expanded={row.expanded}
          onToggle={props.onToggle}
          entryIndex={row.entryIndex}
        />
      )
    case 'group-child':
      return (
        <Rows.Group>
          <ItemRow item={row.item} />
        </Rows.Group>
      )
  }
}

function GroupHeaderRow(props: {
  entry: Extract<ActivityEntry, { type: 'group' }>
  expanded: boolean
  onToggle: (index: number) => void
  entryIndex: number
}) {
  const { entry, expanded } = props

  return (
    <Rows.Row
      action={{ type: 'expand', expanded }}
      height={rowHeight}
      onClick={() => props.onToggle(props.entryIndex)}
    >
      <span className={activityIconClassName({ interactive: true })}>
        <ReceiptText className="size-3.5" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col text-left">
        <span className="text-label-14 text-foreground">
          Access Key activity{' '}
          <span className="hidden sm:inline">· {entry.items.length} transactions</span>
        </span>
        <span className="truncate text-label-14 text-foreground-secondary">
          <span className="sm:hidden">{entry.items.length} transactions · </span>Access Key{' '}
          <Hash value={entry.signer} />
        </span>
      </span>
      <span className="text-label-14 text-foreground-secondary tabular-nums">
        {formatRelativeTime(entry.timestamp)}
      </span>
    </Rows.Row>
  )
}

function explorerUrl(chainId: number, hash: string) {
  const host = chainId === 42431 ? 'explore.moderato.tempo.xyz' : 'explorer.tempo.xyz'
  return `https://${host}/receipt/${hash}`
}

function ItemRow(props: { item: ActivityItem }) {
  const { item } = props
  // Zone activity is private — there is no public explorer for it, so the row
  // is rendered as a non-interactive container instead of a link.
  const isPrivate = item.chainId === Zone.chain.id

  const content = (
    <>
      <ItemIcon interactive={!isPrivate} type={item.type} />

      <div className="flex min-w-0 flex-1 flex-col truncate">
        <span className="flex min-w-0 items-center gap-1.5 text-label-14 text-foreground-secondary">
          <ItemTitle item={item} />
          {isPrivate && (
            <Tag aria-label="Private (zone) activity" intent="info">
              Private
            </Tag>
          )}
        </span>
        <ItemSubtitle item={item} />
      </div>

      <div className="flex shrink-0 flex-col items-end">
        <ItemAmount item={item} />
        <span className="text-label-14 text-foreground-secondary tabular-nums">
          {formatRelativeTime(item.timestamp)}
        </span>
      </div>
    </>
  )

  if (isPrivate) return <Rows.Row height={rowHeight}>{content}</Rows.Row>

  return (
    <Rows.Row
      height={rowHeight}
      render={
        <a href={explorerUrl(item.chainId, item.hash)} rel="noopener noreferrer" target="_blank" />
      }
    >
      {content}
    </Rows.Row>
  )
}

function ItemTitle(props: { item: ActivityItem }) {
  const { item } = props
  const name = item.serviceName ?? displayNames[item.type] ?? 'Transaction'
  const n = (text: string) => <span className="text-foreground">{text}</span>
  switch (item.type) {
    case 'send':
      return (
        <>
          {name} {n(item.amount.symbol)}
        </>
      )
    case 'received':
      return (
        <>
          {name} {n(item.amount.symbol)}
        </>
      )
    case 'mint':
      return (
        <>
          {name} {n(item.amount.symbol)}
        </>
      )
    case 'burn':
      return (
        <>
          {name} {n(item.amount.symbol)}
        </>
      )
    case 'deposit':
      return (
        <>
          {name} {n(item.amount.symbol)}
        </>
      )
    case 'withdrawal':
      return (
        <>
          {name} {n(item.amount.symbol)}
        </>
      )
    case 'swap':
      return (
        <>
          {name} {n(item.amountIn.symbol)} for {n(item.amountOut.symbol)}
        </>
      )
    case 'approval':
      return (
        <>
          {name} {n(item.amount.symbol)}
        </>
      )
    case 'session-closed':
      return <>{name}</>
    case 'access-key-created':
      return <>{name}</>
    case 'access-key-revoked':
      return <>{name}</>
    case 'token-created':
      return (
        <>
          {name} · {n(item.name)} ({n(item.symbol)})
        </>
      )
  }
}

function ItemSubtitle(props: { item: ActivityItem }) {
  const { item } = props
  const n = (text: string) => <span className="text-foreground">{text}</span>
  const addr = (address: string) => <CopyableAddress address={address} />
  switch (item.type) {
    case 'send':
      return <span className="text-label-13 text-foreground-secondary">to {addr(item.to)}</span>
    case 'received':
      return <span className="text-label-13 text-foreground-secondary">from {addr(item.from)}</span>
    case 'mint':
      return null
    case 'burn':
      return null
    case 'deposit':
      return <span className="text-label-13 text-foreground-secondary">to {addr(item.vault)}</span>
    case 'withdrawal':
      return (
        <span className="text-label-13 text-foreground-secondary">from {addr(item.vault)}</span>
      )
    case 'swap':
      return null
    case 'approval':
      return (
        <span className="text-label-13 text-foreground-secondary">
          with spender {addr(item.spender)}
        </span>
      )
    case 'session-closed':
      if (item.refundAmount)
        return (
          <span className="text-label-13 text-foreground-secondary">
            {n(item.refundAmount.formatted)} {n(item.refundAmount.symbol)} refunded
          </span>
        )
      return null
    case 'access-key-created':
      return <span className="text-label-13 text-foreground-secondary">{addr(item.publicKey)}</span>
    case 'access-key-revoked':
      return <span className="text-label-13 text-foreground-secondary">{addr(item.publicKey)}</span>
    case 'token-created':
      return null
  }
}

function ItemAmount(props: { item: ActivityItem }) {
  const { item } = props
  const className = 'max-sm:max-w-[min(36vw,150px)] sm:min-w-max sm:max-w-none'
  switch (item.type) {
    case 'send':
      return <Amount align="right" amount={item.amount} className={className} sign="−" size="md" />
    case 'received':
      return <Amount align="right" amount={item.amount} className={className} sign="+" size="md" />
    case 'mint':
      return <Amount align="right" amount={item.amount} className={className} sign="+" size="md" />
    case 'burn':
      return <Amount align="right" amount={item.amount} className={className} sign="−" size="md" />
    case 'deposit':
      return <Amount align="right" amount={item.amount} className={className} sign="−" size="md" />
    case 'withdrawal':
      return <Amount align="right" amount={item.amount} className={className} sign="+" size="md" />
    case 'swap':
      return <Amount align="right" amount={item.amountOut} className={className} size="md" />
    case 'approval': {
      if (BigInt(item.amount.amount) >= maxUint256 / 2n)
        return <span className="text-label-14 text-foreground-secondary">Unlimited</span>
      return <Amount align="right" amount={item.amount} className={className} size="md" />
    }
    case 'session-closed':
      if (item.refundAmount)
        return (
          <Amount
            align="right"
            amount={item.refundAmount}
            className={className}
            sign="+"
            size="md"
          />
        )
      return null
    default:
      return null
  }
}

const icons: Record<string, React.ComponentType<{ className?: string }>> = {
  send: ArrowUpRight,
  received: ArrowDownLeft,
  mint: ArrowDownToLine,
  burn: Flame,
  deposit: ArrowDownToLine,
  withdrawal: ArrowDownLeft,
  swap: ArrowLeftRight,
  approval: ShieldCheck,
  'session-closed': Timer,
  'access-key-created': Key,
  'access-key-revoked': X,
  'token-created': Sparkles,
}

function ItemIcon(props: { interactive: boolean; type: string }) {
  const Icon = icons[props.type] ?? ArrowUpRight
  return (
    <span className={activityIconClassName({ interactive: props.interactive })}>
      <Icon className="size-3.5" />
    </span>
  )
}

function activityIconClassName(
  options: { interactive?: boolean | undefined; shape?: 'button' | 'identicon' | undefined } = {},
) {
  const { interactive = false, shape = 'identicon' } = options
  return cx(
    'flex size-6 shrink-0 items-center justify-center bg-secondary text-foreground-secondary',
    shape === 'button' ? 'rounded-button' : 'rounded-identicon',
    interactive &&
      'group-hover/rows-row:bg-secondary-hover group-hover/rows-row:text-foreground-secondary-hover group-active/rows-row:bg-secondary-active group-active/rows-row:text-foreground',
  )
}

function CopyableAddress(props: { address: string }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-foreground"
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      <Hash value={props.address} />
      <Copy size="xs" value={checksumAddressForCopy(props.address)} />
    </span>
  )
}

function SkeletonRow() {
  return (
    <Rows.Row height={rowHeight}>
      <div className="size-6 animate-pulse rounded-identicon bg-skeleton" />
      <div className="flex flex-1 flex-col gap-1">
        <div className="h-3.5 w-24 animate-pulse rounded-body bg-skeleton" />
        <div className="h-3 w-16 animate-pulse rounded-body bg-skeleton" />
      </div>
      <div className="flex flex-col items-end gap-1">
        <div className="h-3.5 w-16 animate-pulse rounded-body bg-skeleton" />
        <div className="h-3 w-12 animate-pulse rounded-body bg-skeleton" />
      </div>
    </Rows.Row>
  )
}

function formatRelativeTime(timestamp: number): string {
  const diff = timestamp - Date.now()
  const absDiff = Math.abs(diff)
  if (absDiff < 60_000) return 'just now'
  if (absDiff < 3_600_000) return relative.format(Math.round(diff / 60_000), 'minute')
  if (absDiff < 86_400_000) return relative.format(Math.round(diff / 3_600_000), 'hour')
  if (absDiff < 604_800_000) return relative.format(Math.round(diff / 86_400_000), 'day')
  return new Date(timestamp).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}
